/**
 * `<x-media-zoom>` — `media-zoom.liquid`. Inlines width/max-width % from `data-scale-*`,
 * animates `max-width` after lazy media loads and ≥50% of the host is visible.
 * Optional `data-zoom-autoplay="true"`: after the zoom transition finishes, play in-view
 * videos that are still paused (e.g. after user gesture / scroll).
 */

const DEFAULT_ZOOM_MS = 800
const LAZY_REFRESH_PAD_MS = 100
const HALF_VISIBLE = 0.5
const SCALE_EPSILON = 0.01

function readScale(value: string | undefined, fallback: number): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function whenMediaLoaded(el: HTMLImageElement | HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    if (el instanceof HTMLImageElement) {
      el.addEventListener('load', () => resolve(), { once: true })
      el.addEventListener('error', () => resolve(), { once: true })
    } else {
      el.addEventListener('loadeddata', () => resolve(), { once: true })
      el.addEventListener('error', () => resolve(), { once: true })
    }
  })
}

function parseZoomAutoplay(value: string | undefined): boolean {
  if (value === undefined) return false
  const v = value.trim().toLowerCase()
  return v === 'true' || v === '1' || v === 'yes' || v === 'on'
}

function playVisibleVideosIn(host: HTMLElement) {
  host.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
    if (video.getClientRects().length === 0) return
    if (video.paused) {
      video.play().catch(() => {})
    }
  })
}

export class MediaZoomElement extends HTMLElement {
  private observer?: IntersectionObserver
  private lazyRefreshRafId = 0
  private zoomEndPct = 100
  private zoomDurationMs = DEFAULT_ZOOM_MS
  private zoomAutoplay = false
  private zoomAutoplayTimeoutId = 0
  private onAutoplayTransitionEnd?: (e: TransitionEvent) => void

  private maxWidthTransition(): string {
    return this.zoomDurationMs > 0
      ? `max-width ${this.zoomDurationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : 'none'
  }

  private scheduleLazyLoadRefresh() {
    const run = () => window.lazyLoadInstance?.update()
    queueMicrotask(run)
    cancelAnimationFrame(this.lazyRefreshRafId)
    this.lazyRefreshRafId = requestAnimationFrame(run)
  }

  private clearPendingZoomAutoplay() {
    if (this.zoomAutoplayTimeoutId) {
      window.clearTimeout(this.zoomAutoplayTimeoutId)
      this.zoomAutoplayTimeoutId = 0
    }
    if (this.onAutoplayTransitionEnd) {
      this.removeEventListener('transitionend', this.onAutoplayTransitionEnd)
      this.onAutoplayTransitionEnd = undefined
    }
  }

  /**
   * When `data-zoom-autoplay` is true, start video(s) after the max-width zoom ends
   * (or immediately if there is no transition).
   */
  private scheduleAutoplayWhenZoomSettles() {
    this.clearPendingZoomAutoplay()
    if (!this.zoomAutoplay) return

    if (this.zoomDurationMs <= 0) {
      queueMicrotask(() => {
        if (this.isConnected) playVisibleVideosIn(this)
      })
      return
    }

    const done = () => {
      this.clearPendingZoomAutoplay()
      if (this.isConnected) playVisibleVideosIn(this)
    }

    this.onAutoplayTransitionEnd = (e: TransitionEvent) => {
      if (e.target !== this || e.propertyName !== 'max-width') return
      done()
    }
    this.addEventListener('transitionend', this.onAutoplayTransitionEnd)

    this.zoomAutoplayTimeoutId = window.setTimeout(
      done,
      this.zoomDurationMs + 150
    )
  }

  private snapMaxToEnd() {
    this.style.transition = 'none'
    this.style.maxWidth = `${this.zoomEndPct}%`
    this.clearPendingZoomAutoplay()
    if (this.zoomAutoplay) {
      queueMicrotask(() => {
        if (this.isConnected) playVisibleVideosIn(this)
      })
    }
  }

  private expand() {
    this.style.transition = this.maxWidthTransition()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.maxWidth = `${this.zoomEndPct}%`
        this.scheduleAutoplayWhenZoomSettles()
      })
    })
    this.scheduleLazyLoadRefresh()
    window.setTimeout(() => this.scheduleLazyLoadRefresh(), this.zoomDurationMs + LAZY_REFRESH_PAD_MS)
  }

  private whenLazyMediaReady(): Promise<void> {
    const waits: Array<Promise<void>> = []

    this.querySelectorAll<HTMLImageElement | HTMLVideoElement>('img, video').forEach((el) => {
      if (el.getClientRects().length === 0) return

      if (el instanceof HTMLImageElement) {
        if (!el.classList.contains('lazy') && !el.dataset.src) return
        waits.push(whenMediaLoaded(el))
        return
      }

      if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return
      if (!el.classList.contains('lazy') && !el.dataset.src) return
      waits.push(whenMediaLoaded(el))
    })

    return waits.length === 0 ? Promise.resolve() : Promise.all(waits).then(() => undefined)
  }

  private attachIntersectionObserver() {
    let first = true

    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (!entry) return

      const { intersectionRatio: ratio, isIntersecting } = entry

      if (first) {
        first = false
        if (isIntersecting && ratio >= HALF_VISIBLE) {
          this.snapMaxToEnd()
          this.disconnectObserver()
          this.scheduleLazyLoadRefresh()
        }
        return
      }

      if (!isIntersecting || ratio < HALF_VISIBLE) return
      this.disconnectObserver()
      this.expand()
    }, { threshold: [HALF_VISIBLE] })

    this.observer.observe(this)
  }

  private disconnectObserver() {
    this.observer?.disconnect()
    this.observer = undefined
  }

  connectedCallback() {
    const startPct = readScale(this.dataset.scaleStart, 100)
    this.zoomEndPct = readScale(this.dataset.scaleEnd, 100)
    this.zoomAutoplay = parseZoomAutoplay(this.dataset.zoomAutoplay)
    this.zoomDurationMs = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : DEFAULT_ZOOM_MS

    this.style.width = `${this.zoomEndPct}%`
    this.style.maxWidth = `${startPct}%`
    this.style.transition = this.maxWidthTransition()

    this.scheduleLazyLoadRefresh()

    if (Math.abs(startPct - this.zoomEndPct) < SCALE_EPSILON) {
      return
    }

    this.whenLazyMediaReady().then(() => {
      if (!this.isConnected) return
      this.scheduleLazyLoadRefresh()
      this.attachIntersectionObserver()
    })
  }

  disconnectedCallback() {
    this.clearPendingZoomAutoplay()
    cancelAnimationFrame(this.lazyRefreshRafId)
    this.lazyRefreshRafId = 0
    this.disconnectObserver()
  }
}
